import { hashKey, type InfiniteData, useQueryClient, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { isInputDateValue, parseDateInputValue } from '../../utils/date.ts';
import { request } from '../axios';
import type { TaskDto } from '../generated/model';
import { getPaginatedContent, type PaginatedResponse } from '../generated/pagination/pagination.types';

type RequestOptions = Parameters<typeof request>[1];

export const TASK_PAGE_SIZE = 20;

export type TaskSortField = 'due';
export type TaskSortDirection = 'asc' | 'desc';

export type GetPendingTasksParams = {
  page?: number;
  size?: number;
  includeCompleted?: boolean;
  dueDate?: string;
  assignedEmployeeId?: string;
  patientId?: string;
  sortBy?: TaskSortField;
  sortDir?: TaskSortDirection;
};

export function toTaskDueDateQueryParam(value?: string | null) {
  if (!value) {
    return undefined;
  }

  const normalized = parseDateInputValue(value);
  return normalized && isInputDateValue(normalized) ? normalized : undefined;
}

export const getPendingTasksPaginated = (
  params?: GetPendingTasksParams,
  options?: RequestOptions,
  signal?: AbortSignal,
) => {
  return request<PaginatedResponse<TaskDto>>({ url: `/api/tasks`, method: 'GET', params, signal }, options);
};

export const getGetPendingTasksQueryKey = (params?: Omit<GetPendingTasksParams, 'page'>) => {
  return [`/api/tasks`, ...(params ? [params] : [])] as const;
};

const getNextTasksPageParam = (lastPage: PaginatedResponse<TaskDto>, allPages: PaginatedResponse<TaskDto>[]) => {
  const loadedCount = allPages.reduce((count, page) => count + getPaginatedContent(page).length, 0);
  const nextPage = allPages.length;

  if (lastPage.totalCount > 0) {
    return loadedCount < lastPage.totalCount ? nextPage : undefined;
  }

  // Fall back to page fullness when the backend does not report a usable total.
  return getPaginatedContent(lastPage).length === TASK_PAGE_SIZE ? nextPage : undefined;
};

export type UseGetPendingTasksInfiniteSuspenseFilters = Omit<GetPendingTasksParams, 'page' | 'size'>;

type TaskPagesData = InfiniteData<PaginatedResponse<TaskDto>, number>;

export function useGetPendingTasksInfiniteSuspense(filters?: UseGetPendingTasksInfiniteSuspenseFilters) {
  const queryClient = useQueryClient();
  const baseParams = { size: TASK_PAGE_SIZE, ...filters };
  const queryKey = [...getGetPendingTasksQueryKey(baseParams), 'infinite'] as const;
  const queryKeyHash = hashKey(queryKey);

  const query = useSuspenseInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => getPendingTasksPaginated({ ...baseParams, page: pageParam }, undefined, signal),
    initialPageParam: 0,
    getNextPageParam: getNextTasksPageParam,
  });

  // Infinite queries refetch every cached page. Keep only page 0 when this query is no longer
  // shown (route change, filter change, or suspense) so the next view is a single request.
  useEffect(() => {
    const key = queryKey;
    return () => {
      const state = queryClient.getQueryState<TaskPagesData>(key);
      if (!state?.data || state.data.pages.length <= 1) {
        return;
      }

      queryClient.setQueryData<TaskPagesData>(
        key,
        { pages: state.data.pages.slice(0, 1), pageParams: state.data.pageParams.slice(0, 1) },
        { updatedAt: state.dataUpdatedAt },
      );
    };
    // queryKeyHash is the stable identity of queryKey; including queryKey would trim on every render.
  }, [queryClient, queryKeyHash]);

  const tasks = useMemo<TaskDto[]>(() => query.data.pages.flatMap((page) => getPaginatedContent(page)), [query.data]);

  return { ...query, tasks };
}
